/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Ref } from 'preact/hooks';
import { Group } from '../../UNSAFE_Axis/axis.types';
import { getInfo } from '../../utils/UNSAFE_visUtils';
import { ChartItem, GroupInfo, Info } from '../../UNSAFE_Chart';

type DrillEventsProps<D> = {
  rootRef: Ref<HTMLDivElement>;
  groups: Group[];
  focusedItemInfo: Info;
  focusedGroupInfo: GroupInfo;
  selectionMode: 'none' | 'single' | 'multiple';
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined;
  drilling: 'on' | 'groupsOnly' | 'off';
  onItemDrill?: (detail: any) => void;
  onGroupDrill?: (detail: any) => void;
};

const isDrillable = <K extends string | number, D extends ChartItem<K>>(
  info: { seriesIndex?: number; groupIndex?: number },
  drilling: 'on' | 'groupsOnly' | 'off',
  groups: Group[],
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined
) => {
  const { seriesIndex, groupIndex } = info;

  if (groupIndex === undefined) {
    return false;
  }

  if (seriesIndex != null) {
    const itemDrilling = getDataItem(seriesIndex, groupIndex)?.drilling != 'off';
    return drilling === 'on' && itemDrilling;
  } else {
    return groups[groupIndex].drilling != 'off';
  }
};

/**
 * Returns the event handlers for drill support.
 */
export function useChartDrill<K extends string | number, D extends ChartItem<K>>({
  rootRef,
  focusedItemInfo,
  focusedGroupInfo,
  groups,
  selectionMode,
  drilling,
  getDataItem,
  onItemDrill,
  onGroupDrill
}: DrillEventsProps<D>) {
  const processDrill = (info?: { seriesIndex?: number; groupIndex?: number }) => {
    if (
      !info ||
      info.groupIndex == undefined ||
      !isDrillable(info, drilling, groups, getDataItem)
    ) {
      return;
    }

    const { seriesIndex, groupIndex } = info;

    if (seriesIndex != null) {
      const data = getDataItem(seriesIndex, groupIndex);
      onItemDrill?.({
        groupIndex,
        seriesIndex,
        data,
        id: data?.id
      });
    } else {
      onGroupDrill?.({
        groupIndex,
        group: groups[groupIndex],
        id: groups[groupIndex].id
      });
    }
  };

  const onPointerUp = (event: PointerEvent) => {
    if (selectionMode != 'none') {
      return;
    }
    processDrill(getInfo(rootRef, event));
  };

  const onDblClick = (event: MouseEvent) => {
    if (selectionMode === 'none') {
      return;
    }
    processDrill(getInfo(rootRef, event));
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key != 'Enter') {
      return;
    }
    const focusedInfo = focusedItemInfo.isCurrent ? focusedItemInfo : focusedGroupInfo;
    processDrill(focusedInfo);
  };

  return {
    onPointerUp,
    onDblClick,
    onKeyDown
  };
}
